import { ErrorHandler, Inject, Injectable, OnDestroy } from '@angular/core';
import { isEqual, isNil, sumBy } from 'lodash';
import { EMPTY, Subject, Subscription, catchError, from, interval, merge, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthUserService } from '../auth/auth-user.service';
import {  ReservationProduct, UpdateCartAttributes, UpdateCartItemPayload } from './models';
import { CustomerViewModel } from '../customer/customer.service';
import { ErrorNotificationsService } from '../core/error-handling/error-notifications.service';
import { DepositMoney, WithdrawMoney } from './models';
import {
  CompletedPaymentResult,
  PaymentMethodPayload,
  PaymentMethodType,
  PaymentSubmitPayload,
} from './models/payment.models';
import { Transaction } from './models';
import { AppStateLoaderService } from './app-state-loader.service';
import { BackendService } from './backend.service';
import { COUNTRIES, Country } from './countries';
import {
  Cart,
  CustomerByEmail,
  CartCustomerUser,
  CashBox,
  Collection,
  PaginatedCollection,
  PrinterTypes,
  Product,
  Settings,
  User,
} from './models/models';
import { PusherService } from './pusher.service';
import { APP_SETTINGS } from './settings-data.provider';
import { PosSoundService } from '../core/pos-sound.service';


export function createEmptyCustomerByEmail(): CustomerByEmail {
  return {
    email: '',
    acceptsNewsletter: ''
  };
}

@Injectable({ providedIn: 'root' })
export class DataService implements OnDestroy {
  dataPollInterval: any;
  subscription: Subscription;

  cart!: Cart;
  posCustomer: CustomerByEmail = createEmptyCustomerByEmail();
  barcodes!: Record<string, number>;
  cashBox!: CashBox;
  products: Product[] = [];

  reservationsAndPickupsCount: number = 0;

  private _reservationsAndPickupsChanged = new Subject<void>();
  reservationsAndPickupsChanged = this._reservationsAndPickupsChanged.asObservable();

  completedPaymentResult: CompletedPaymentResult | null = null;

  constructor(private dataLoader: AppStateLoaderService,
    @Inject(APP_SETTINGS) private readonly settings: Settings,
    private readonly userService: AuthUserService,
    private readonly backend: BackendService,
    private readonly pusher: PusherService,
    private readonly errorHandler: ErrorHandler,
    private readonly errorNotifications: ErrorNotificationsService,
    private readonly sound: PosSoundService,
  ) {
    this.setDataFromLoader();
    this.subscription = this.dataLoader.updated.subscribe(() => this.setDataFromLoader());
    this.initDataPolling();
  }

  private setDataFromLoader() {
    if (!this.dataLoader.data) {
      throw new Error(`Initial data not loaded. Guard your routes.`);
    }

    this.cart = this.dataLoader.data.cart;
    this.barcodes = this.dataLoader.data.barcodes;
    this.cashBox = this.dataLoader.data.cashBox;
  }

  ngOnDestroy() {
    clearInterval(this.dataPollInterval);
    this.subscription?.unsubscribe();
  }

  initDataPolling() {
    if (environment.pollingDelay === 0 || isNil(environment.pollingDelay)) {
      return;
    }

    // immediate data fetching
    this.fetchReservationsAndPickupsCount({ force: true });

    const fetchReservationsAndPickupsCount = () => from(this.fetchReservationsAndPickupsCount({ force: true, initial: false }))
      .pipe(catchError((error) => {
        if (error.status === 0) {
          // Sometime we get this error when the app is offline, we should ignore it regarding polling
          return EMPTY;
        }

        this.errorHandler.handleError(error);
        return EMPTY;
      }));

    // polling and reaction to pusher events
    this.userService.reservationSubscription = merge(interval(environment.pollingDelay), this.pusher.reservation).pipe(
      switchMap(fetchReservationsAndPickupsCount)
    ).subscribe();
    this.userService.pickupSubscription = merge(interval(environment.pollingDelay), this.pusher.pickup).pipe(
      switchMap(fetchReservationsAndPickupsCount)
    ).subscribe();
  }

  async fetchCountries(): Promise<Country[]> {
    // Easier to change to real backend source if already handled as async call
    return Promise.resolve(COUNTRIES);
  }

  async fetchAllProducts(): Promise<Product[]> {
    this.products = await this.backend.fetchAllProducts();
    return this.products;
  }

  async fetchPaginatedProducts(filter: {search?: string; p: number}) {
    return await this.backend.fetchPaginatedProducts(filter);
  }

  async fetchPaginatedPickedups(filter: {p: number}) {
    return await this.backend.fetchPaginatedPickedups(filter);
  }

  async fetchPaginatedPickupsAndReservations(filter: {p: number}) {
    return await this.backend.fetchPaginatedPickupsAndReservations(filter);
  }

  async fetchProduct(id: number): Promise<Product> {
    const product = this.products.find(p => p.id === id);
    if (product) {
      return Promise.resolve(product);
    }

    return this.backend.fetchProduct(id);
  }

  fetchCollection(collectionId: number): Promise<Collection> {
    return this.backend.fetchCollection(collectionId);
  }

  fetchPaginatedCollection(collectionId: number, params?: { [key: string]: string | number }): Promise<PaginatedCollection> {
    return this.backend.fetchPaginatedCollection(collectionId, params);
  }

  async addToCart(productId: number): Promise<void> {
    const previousCart = JSON.stringify(this.cart);
    const newCart = await this.backend.addToCart(productId);
    if (JSON.stringify(newCart) === previousCart) {
      const message = $localize`Das Produkt ist nicht mehr verfügbar.`;
      this.errorNotifications.showError(message);
      setTimeout(() => window.location.reload(), 10000);
    } else {
      this.cart = newCart;
      this.sound.addToCart();
    }
  }

  async addAdHocProductToCart(payload: { title: string; price: number; taxRate: number }): Promise<void> {
    this.cart = await this.backend.addAdHocProductToCart({
      title: payload.title,
      price: payload.price,
      // FIXME: tax rate currently does not recognised by the API
      tax_rate: payload.taxRate
    });
    this.sound.addToCart();
  }

  async removeFromCart(productId: number): Promise<void> {
    this.cart = await this.backend.updateCart({
      id: productId,
      quantity: 0,
      type: 'cartItemUpdate'
    });
    this.sound.remove();
  }

  async clearCart(): Promise<void> {
    this.cart = await this.backend.clearCart();
  }

  async updateCartProduct(changes: Omit<UpdateCartItemPayload, 'type'>): Promise<void> {
    this.cart = await this.backend.updateCart({
      ...changes,
      type: 'cartItemUpdate'
    });
  }

  async updateCartCoupon(couponCode: string): Promise<void> {
    this.cart = await this.backend.updateCart({
      couponCode,
      type: 'cartCouponCodeUpdate'
    });
  }

  async updateCartNote(note: string | null): Promise<void> {
    this.cart = await this.backend.updateCart({
      note,
      type: 'cartNoteUpdate'
    });
  }

  async updateCartAttributes(attributes: Omit<UpdateCartAttributes, 'type'>): Promise<void> {
    this.cart = await this.backend.updateCart({
      ...attributes,
      type: 'cartAttributesUpdate'
    });
  }

  async updateCartCustomer(customer: CustomerByEmail | null): Promise<void> {
    const customerData = customer ?? createEmptyCustomerByEmail();

    this.cart = await this.backend.updateCart({
      customer: customerData,
      type: 'cartCustomerUpdate'
    });
  }

  async updatePOSCustomer(customer: CustomerByEmail | null): Promise<void> {
    this.posCustomer = customer ?? createEmptyCustomerByEmail();
  }

  async fetchPaginatedTransactionHistory(filter: {search?: string; p: number}) {
    return await this.backend.fetchPaginatedTransactionHistory(this.settings.posUuid, filter);
  }

  fetchTransactionHistoryRecord(id: number): Promise<Transaction> {
    return this.backend.fetchTransactionHistoryRecord(this.settings.posUuid, id);
  }

  hasProductsAvailableToReturn<T extends Transaction>(transaction: T | null) {
    if (!transaction) {
      return false;
    }
    if (!this.isSPaymentReturnPossible(transaction)) {
      return false;
    }
    return transaction.items.some(item => (item.returnedQuantity ?? 0) < 1);
  }

  private isSPaymentReturnPossible<T extends Transaction>(transaction: T) {
    // reversal or refund can happen only once; the S-Payment POS reversal leaves an explicit  trace of the event (cash reversal does not)
    return !this.isSPaymentTransactionReversed(transaction) && !this.hasSPaymentTransactionRefund(transaction);
  }

  private isSPaymentTransactionReversed<T extends Transaction>(transaction: T) {
    return !!(transaction.cartAttributes?.spos_reversal_transaction_data);
  }

  private hasSPaymentTransactionRefund<T extends Transaction>(transaction: T) {
    return !!(transaction.cartAttributes?.spos_refund_transaction_data);
  }

  async checkoutCart(payment: PaymentMethodPayload): Promise<void> {
    if (!this.userService.authenticatedUser) {
      throw new Error('Can not checkout cart if user is not logged in.');
    }

    const payload = this.preparePaymentSubmitPayload(payment, this.userService.authenticatedUser);

    const completedPayment = await this.backend.checkoutCart(payload);
    this.completedPaymentResult = {
      completedPayment,
      paymentMethod: payment.paymentMethod,
      type: payment.method,
      receivedAmount: payment.method === PaymentMethodType.cash ? payment.receivedAmount : completedPayment.total,
      isSPOS: payment.isSPOS,
    };
    await this.refreshCashBox();
  }

  preparePaymentSubmitPayload(payment: PaymentMethodPayload, { id: user_id }: User): PaymentSubmitPayload {
    const { paymentMethod: { gateway: payment_type }, method } = payment;
    const { reservationId: reservation_id } = this.cart;
    let is_reservation!: boolean;

    if (reservation_id) {
      is_reservation = true;
    }

    const payload = { user_id, payment_type, is_reservation, reservation_id };

    switch (method) {
      case PaymentMethodType.creditCard:
        return { ...payload, card_type: payment.cardType };
      case PaymentMethodType.cash:
        return { ...payload, received: payment.receivedAmount };
      default:
        throw new Error(`Could not convert payment to valid payload: Received ${JSON.stringify(payment)}`);
    }
  }

  async refreshCashBox() {
    this.cashBox = await this.backend.fetchCashBox(this.settings.posUuid);
  }

  // Clear cart and removes completed payments data
  async startNewBuying(): Promise<void> {
    await this.clearCart();
    this.completedPaymentResult = null;
  }

  async openCashBox(currentAmount: number): Promise<boolean> {
    if (!this.userService.authenticatedUser) {
      throw new Error('Can not open cash-box if user is not logged in.');
    }
    const { posUuid } = this.settings;
    const { id: userId } = this.userService.authenticatedUser;

    this.cashBox = await this.backend.openCashBox(posUuid, { currentAmount, userId });

    return this.cashBox.status === 'open';
  }

  async openCashDrawer() {
    const { posUuid, printerType } = this.settings;
    const action = printerType === PrinterTypes.PRINTERBOX ? 'openPrinterboxCashDrawer' : 'openCashDrawer';

    return await this.backend[action](posUuid);
  }

  async closeCashBox(amount: number, sposMerchantReceipt?: string, opiMerchantReceipt?: string): Promise<boolean> {
    if (!this.userService.authenticatedUser) {
      throw new Error('Can not close cash-box if user is not logged in.');
    }

    this.cashBox = await this.backend.closeCashBox(this.settings.posUuid, {
      currentAmount: amount,
      userId: this.userService.authenticatedUser.id,
      sposMerchantReceipt: sposMerchantReceipt,
      opiMerchantReceipt: opiMerchantReceipt
    });

    return this.cashBox.status === 'closed';
  }

  createNewCustomer({
    email,
    addToNewsletter: accepts_newsletter,
    registerCustomer: registered,
    title,
    firstName: firstname,
    lastName: lastname,
    company,
    tel: phone,
    street,
    houseNumber: streetnumber,
    addressSupplement: extension,
    postcode: zipcode,
    place: city,
    country,
  }: CustomerViewModel): Promise<CartCustomerUser[]> {
    return this.backend.createCustomer({
      email, accepts_newsletter, registered,
      billing_address: {
        title, firstname, lastname,
        company, phone, street,
        streetnumber, extension,
        zipcode, city, email, country,
      },
    });
  }

  // Indications
  get orderedItemsCount(): number {
    if (!this.cart) {
      return 0;
    }
    return sumBy(this.cart.lineItems, item => item.quantity);
  }

  get hasEntryInCart(): boolean {
    return this.cart?.lineItems?.length > 0;
  }

  get cashBoxClosed(): boolean {
    return this.cashBox?.status !== 'open';
  }

  get minAppVersion(): string {
    return this.settings?.minAppVersion ?? '0.0.0';
  }

  async cashBoxTransferAction(payload: DepositMoney | WithdrawMoney) {
    if (!this.userService.authenticatedUser) {
      throw new Error('Can not change cash-box if user is not logged in.');
    }
    const { id: userId } = this.userService.authenticatedUser;
    const { posUuid } = this.settings;

    this.cashBox = await this.backend.cashBoxAddTransaction(posUuid, { ...payload, userId });
  }

  async searchCustomer(name: string) {
    return this.backend.searchCustomer(name);
  }

  get reservationAndPickupCount(): number {
    return this.reservationsAndPickupsCount ?? 0;
  }

  async fetchReservationsAndPickupsCount({ force = false, initial = true } = {}) {
    if (force) {
      const { count } = await this.backend.fetchReservationsAndPickupsCount();
      const changed = !isEqual(count, this.reservationsAndPickupsCount);
      this.reservationsAndPickupsCount = count || 0;
      if (!initial && changed) {
        this._reservationsAndPickupsChanged.next();
      }
    }

    return this.reservationsAndPickupsCount;
  }

  async addReservedProductToCart(reservationId: number, products: ReservationProduct[], email: string, acceptsNewsletter: boolean) {
    if (!this.userService.authenticatedUser) {
      throw new Error('Can not order reserved item if user is not logged in.');
    }
    const { id } = this.userService.authenticatedUser;

    this.cart = await this.backend.addReservationToCart(reservationId, id, products, email, acceptsNewsletter);
    this.sound.addToCart();
  }

  async completePickup(orderId: number) {
    if (!this.userService.authenticatedUser) {
      throw new Error('Can not finish pickup item if user is not logged in.');
    }
    const { id: userId } = this.userService.authenticatedUser;

    return await this.backend.markPickupAsCompleted(orderId, userId);
  }

  async deleteReservation(reservationId: number) {
    return await this.backend.deleteReservation(reservationId);
  }
}
